import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FirebaseContext, LocalContext } from 'context';

function ReactionStream({ eventId, livestreamRef }) {
  const { firebase } = useContext(FirebaseContext);
  const { browserTabIsActive } = useContext(LocalContext);
  const [reactionsStream, setReactionsStream] = useState([]);

  const getRandomNumber = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);

  const handleEmojiAnimationEnd = (_key) => {
    setReactionsStream((currentState) => currentState.filter(({ key }) => key !== _key));
  };

  const getEmojiVariants = (animationSteps) => {
    if (livestreamRef?.current) {
      return {
        initial: {
          opacity: 0
        },
        animate: {
          x: [
            0,
            animationSteps.x[0],
            animationSteps.x[1],
            animationSteps.x[2],
            animationSteps.x[3]
          ],
          y: -(livestreamRef.current.offsetHeight - 10),
          scale: [0.25, 1, 0.75, 1, 0.75],
          rotate: [
            0,
            animationSteps.rotate[0],
            animationSteps.rotate[1],
            animationSteps.rotate[2],
            animationSteps.rotate[3]
          ],
          opacity: [0, 0.5, 1, 1, 0.5, 0]
        }
      };
    }
  };

  useEffect(() => {
    let unsubscribeFromEmojis;

    // The below check for 'browserTabIsActive' is so that reaction emojis are only shown when the
    // website is in focus. Otherwise, if someone is on another tab and then goes back to the
    // livestream, they would be instantly hit with a huge stream of every emoji triggered since the
    // site was last in focus.
    if (firebase && browserTabIsActive) {
      unsubscribeFromEmojis = firebase.interaction.emojis.subscribeToEmojis({
        eventId,
        snapshot: (snapshot) => {
          if (snapshot.exists()) {
            // This is just to get a summary of all the emojis. For my own reference or for analytics dashboard in the future.
            // console.log(Object.values(snapshot.val()).reduce((acc, { emojiType }) => {
            // acc[emojiType] = acc[emojiType] + 1 || 1
            // return acc;
            // }, {}));

            const { emojiType } = snapshot.val();
            // We're not using the useWindowSize hook below coz if we did we'd have to pass it's
            // return value into the dependency array of this useEffect, which would then trigger all the
            // subscription code again.
            const { innerWidth } = window;
            setReactionsStream((currentState) => [
              ...currentState,
              {
                emojiType,
                key: snapshot.key,
                // TODO: DRY refactor below
                animationSteps: {
                  x: [
                    getRandomNumber(innerWidth < 550 ? -40 : -100, 0),
                    getRandomNumber(innerWidth < 550 ? -40 : -100, 0),
                    getRandomNumber(innerWidth < 550 ? -40 : -100, 0),
                    getRandomNumber(innerWidth < 550 ? -40 : -100, 0)
                  ],
                  rotate: [
                    getRandomNumber(-45, 45),
                    getRandomNumber(-45, 45),
                    getRandomNumber(-45, 45),
                    getRandomNumber(-45, 45)
                  ]
                }
              }
            ]);
          }
        }
      });
    }

    return () => {
      if (unsubscribeFromEmojis) {
        unsubscribeFromEmojis();
      }
      setReactionsStream([]);
    };
  }, [firebase, browserTabIsActive]);

  return (
    <Wrapper>
      <div>
        {reactionsStream.map(({ emojiType, key, animationSteps }) => {
          /* TODO: Refactor these so they're the same svgs as <EmojiSmile /> etc imported at top of this page. (I'll have to refactor those svgs to be Framer Motion components, with conditional checks for the variants) */
          switch (emojiType) {
            case 'smile':
              return (
                <motion.svg
                  key={key}
                  variants={getEmojiVariants(animationSteps)}
                  initial="initial"
                  animate="animate"
                  transition={{ duration: 2.5 }}
                  times={{ opacity: [0, 0.25, 0.5, 0.6, 0.75, 1] }}
                  onAnimationComplete={() => handleEmojiAnimationEnd(key)}
                  width="70"
                  height="70"
                  viewBox="0 0 70 70"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M69.4114 37.1324C69.4114 57.8775 55.4135 69.5364 34.7057 69.5364C13.998 69.5364 0 57.8775 0 37.1324C0 15.5993 13.2344 0 34.7057 0C56.177 0 69.4114 15.5993 69.4114 37.1324Z"
                    fill="#FED132"
                  />
                  <path
                    d="M52.1049 35.9505C52.1049 46.3577 45.0944 52.1988 34.7058 52.1988C24.3172 52.1988 17.3066 46.3577 17.3066 35.9505C17.3066 25.1491 23.947 17.3379 34.7058 17.3379C45.4645 17.3379 52.1049 25.1491 52.1049 35.9505Z"
                    fill="white"
                  />
                  <path
                    d="M26.5381 29.9011C26.5381 30.5501 26.6075 31.1991 26.7232 31.8249C27.1859 34.1196 30.5177 34.1196 30.9804 31.8249C31.0961 31.1991 31.1655 30.5732 31.1655 29.9011C31.1655 28.9971 31.0498 28.1163 30.7953 27.2818C30.24 25.3116 27.4404 25.3116 26.8851 27.2818C26.6769 28.1163 26.5381 28.9971 26.5381 29.9011Z"
                    fill="#FED132"
                  />
                  <path
                    d="M42.7798 29.9011C42.7798 30.5501 42.7104 31.1991 42.5947 31.8249C42.1319 34.1196 38.8002 34.1196 38.3374 31.8249C38.2218 31.1991 38.1523 30.5732 38.1523 29.9011C38.1523 28.9971 38.268 28.1163 38.5225 27.2818C39.0778 25.3116 41.8774 25.3116 42.4327 27.2818C42.6641 28.1163 42.7798 28.9971 42.7798 29.9011Z"
                    fill="#FED132"
                  />
                  <path
                    d="M27.9487 38.3382C29.3138 39.4972 30.9797 40.8415 34.6816 40.7488C38.1291 40.6561 39.5867 39.9839 41.484 38.3382C41.7616 38.1064 42.1318 38.1528 42.34 38.3846C42.4788 38.5468 42.5251 38.7786 42.4557 38.964C41.3683 42.2323 38.1753 43.5998 34.751 43.6462C30.8409 43.6925 28.0181 41.7223 26.977 38.964C26.8613 38.6627 27.0232 38.315 27.324 38.1991C27.5091 38.1296 27.7405 38.1528 27.9487 38.3382Z"
                    fill="#FED132"
                  />
                </motion.svg>
              );
            case 'love':
              return (
                <motion.svg
                  key={key}
                  variants={getEmojiVariants(animationSteps)}
                  initial="initial"
                  animate="animate"
                  transition={{ duration: 2.5 }}
                  times={{ opacity: [0, 0.25, 0.5, 0.6, 0.75, 1] }}
                  onAnimationComplete={() => handleEmojiAnimationEnd(key)}
                  width="70"
                  height="70"
                  viewBox="0 0 70 70"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M70.0003 37.5963C70.0003 58.3413 56.0023 70.0003 35.2946 70.0003C14.5868 70.0003 0.588867 58.3413 0.588867 37.5963C0.588867 16.0632 13.8233 0.463867 35.2946 0.463867C56.789 0.463867 70.0003 16.0632 70.0003 37.5963Z"
                    fill="#F55151"
                  />
                  <path
                    d="M25.9004 22.2749C30.8286 22.2749 33.4199 25.01 35.3172 27.212C37.2144 25.01 39.7827 22.2749 44.734 22.2749C50.472 22.2749 55.192 27.2583 55.192 33.5166C55.192 42.5564 43.4383 50.6921 37.9779 53.5895C36.3121 54.4703 34.3223 54.4703 32.6564 53.5895C27.196 50.6921 15.4424 42.5564 15.4424 33.5166C15.4424 27.2583 20.1623 22.2749 25.9004 22.2749Z"
                    fill="white"
                  />
                </motion.svg>
              );
            case 'like':
              return (
                <motion.svg
                  key={key}
                  variants={getEmojiVariants(animationSteps)}
                  initial="initial"
                  animate="animate"
                  transition={{ duration: 2.5 }}
                  times={{ opacity: [0, 0.25, 0.5, 0.6, 0.75, 1] }}
                  onAnimationComplete={() => handleEmojiAnimationEnd(key)}
                  width="70"
                  height="70"
                  viewBox="0 0 70 70"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M69.4174 37.1324C69.4174 57.8775 55.4194 69.5364 34.7116 69.5364C14.0039 69.5364 0.00585938 57.8775 0.00585938 37.1324C0.00585938 15.5993 13.2403 0 34.7116 0C56.1829 0 69.4174 15.5993 69.4174 37.1324Z"
                    fill="#0B91FC"
                  />
                  <path
                    d="M48.8481 46.5198C49.8662 46.033 51.4164 45.3145 51.8328 44.1323C52.2493 42.9734 51.532 41.7913 51.3469 40.6555C52.4575 40.3542 53.7301 39.5893 53.9383 37.9204C54.1697 36.1125 52.8509 35.3012 51.4858 34.5131C51.7866 34.2582 51.9948 34.0496 52.2262 33.8873C53.3367 33.0529 53.9152 31.9635 53.4062 30.5959C52.9203 29.2979 51.8328 28.6953 50.4215 28.7416C46.5344 28.8343 42.6242 28.7416 38.7372 28.7416C39.4313 24.2681 40.7501 19.5628 37.0482 15.9237C35.9376 14.8575 33.9016 14.139 33.5776 16.0396C33.1843 18.5198 34.6882 21.4867 33.5545 23.9436C32.3745 26.5396 30.9631 28.4403 29.5286 30.8973C28.8114 32.1257 27.8396 33.2151 27.053 34.3972C26.7985 34.7681 26.6365 35.2549 26.6365 35.6953C26.6134 39.9138 26.6134 44.1092 26.6365 48.3277C26.6365 48.7218 26.7522 49.2549 27.0298 49.4635C28.3718 50.4833 29.7831 51.3178 31.6341 51.2946C36.1459 51.2251 40.6345 51.3178 45.1462 51.2946C47.9227 51.3178 50.8611 49.6953 48.8481 46.5198Z"
                    fill="white"
                  />
                  <path
                    d="M20.0424 52.3607C17.5204 52.3607 15.4844 50.321 15.4844 47.7945V37.3177C15.4844 34.7912 17.5204 32.7515 20.0424 32.7515C22.5643 32.7515 24.6004 34.7912 24.6004 37.3177V47.7945C24.6004 50.321 22.5643 52.3607 20.0424 52.3607Z"
                    fill="white"
                  />
                </motion.svg>
              );
            case 'clap':
              return (
                <motion.svg
                  key={key}
                  variants={getEmojiVariants(animationSteps)}
                  initial="initial"
                  animate="animate"
                  transition={{ duration: 2.5 }}
                  times={{ opacity: [0, 0.25, 0.5, 0.6, 0.75, 1] }}
                  onAnimationComplete={() => handleEmojiAnimationEnd(key)}
                  width="70"
                  height="70"
                  viewBox="0 0 70 70"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M69.93 37.5963C69.93 58.3413 55.932 70.0003 35.2243 70.0003C14.5165 70.0003 0.518555 58.3413 0.518555 37.5963C0.518555 16.0632 13.753 0.463867 35.2243 0.463867C56.6955 0.463867 69.93 16.0632 69.93 37.5963Z"
                    fill="#33CC66"
                  />
                  <path
                    d="M39.713 22.0433C39.7593 22.0433 39.8981 22.0201 40.1294 21.9505C40.3608 21.881 40.6385 21.8115 40.9392 21.6956C41.2632 21.6029 41.5871 21.487 41.9573 21.3711C42.3275 21.2552 42.6514 21.1393 42.9753 21.0234C43.2992 20.9075 43.5769 20.8148 43.8314 20.7221C44.0859 20.6294 44.2479 20.5598 44.3173 20.5366C44.4561 20.4671 44.5718 20.3976 44.6412 20.2817C44.7337 20.189 44.7569 20.0499 44.7569 19.8645C44.7569 19.7717 44.7337 19.7022 44.7106 19.6095C44.6643 19.54 44.6181 19.4704 44.5487 19.4009L41.6796 16.5035C41.5408 16.3645 41.3789 16.2949 41.2169 16.2949C41.0318 16.2949 40.893 16.3413 40.8004 16.4108C40.7079 16.5035 40.6153 16.6194 40.5459 16.7585C40.4996 16.8512 40.4534 17.0135 40.3608 17.2684C40.2683 17.5234 40.1757 17.8015 40.06 18.1029C39.9443 18.4274 39.8287 18.7519 39.713 19.0996C39.5973 19.4472 39.4816 19.7949 39.3891 20.0962C39.2965 20.4207 39.204 20.6757 39.1345 20.9075C39.0651 21.1393 39.042 21.2784 39.042 21.3247C39.042 21.5102 39.1114 21.6492 39.2502 21.7883C39.3659 21.9737 39.5279 22.0433 39.713 22.0433Z"
                    fill="white"
                  />
                  <path
                    d="M31.6372 16.8282C31.7761 17.1295 31.938 17.454 32.1 17.8017C32.2619 18.1494 32.4239 18.4739 32.5859 18.7752C32.7478 19.0765 32.8867 19.3547 33.0023 19.5865C33.118 19.8183 33.2106 19.9805 33.2568 20.0732C33.3263 20.1891 33.4188 20.305 33.5114 20.3514C33.6039 20.4209 33.7196 20.4441 33.8816 20.4441C34.0435 20.4441 34.1592 20.4209 34.2518 20.3514C34.3443 20.2818 34.4137 20.1891 34.4831 20.0732C34.5294 19.9805 34.6219 19.7951 34.7376 19.5633C34.8533 19.3315 35.0153 19.0534 35.1541 18.752C35.3161 18.4507 35.478 18.1262 35.64 17.8017C35.8019 17.4772 35.9639 17.1527 36.1027 16.8514C36.2416 16.5501 36.3572 16.2951 36.4498 16.0633C36.5423 15.8315 36.5886 15.6924 36.5886 15.6461C36.5886 15.4606 36.5192 15.2984 36.3804 15.1825C36.2415 15.0666 36.0796 14.9971 35.8945 14.9971H31.8455C31.6604 14.9971 31.4984 15.0666 31.3596 15.1825C31.2208 15.2984 31.1514 15.4606 31.1514 15.6461C31.1514 15.7156 31.1976 15.8547 31.2902 16.0633C31.3827 16.2719 31.4984 16.5269 31.6372 16.8282Z"
                    fill="white"
                  />
                  <path
                    d="M23.4708 20.5367C23.5403 20.583 23.7022 20.6294 23.9567 20.7221C24.2112 20.8148 24.4889 20.9075 24.8128 21.0234C25.1367 21.1393 25.4838 21.2552 25.8308 21.3711C26.201 21.487 26.5249 21.6029 26.8257 21.6956C27.1265 21.7883 27.4041 21.881 27.6355 21.9506C27.8669 22.0201 28.0057 22.0433 28.0751 22.0433C28.2602 22.0433 28.399 21.9738 28.5378 21.8347C28.6767 21.6956 28.7461 21.5565 28.7461 21.3711C28.7461 21.3016 28.7229 21.1625 28.6535 20.9539C28.5841 20.7221 28.5147 20.4671 28.399 20.1426C28.3065 19.8181 28.1908 19.4936 28.0751 19.1459C27.9594 18.7983 27.8437 18.4506 27.728 18.1493C27.6124 17.8248 27.5198 17.5466 27.4273 17.3148C27.3347 17.0599 27.2653 16.8976 27.2422 16.8049C27.1728 16.6426 27.1033 16.5267 26.9877 16.4572C26.8951 16.3645 26.7563 16.3413 26.5712 16.3413C26.3861 16.3413 26.2473 16.4108 26.1085 16.5499L23.2395 19.4473C23.1701 19.5168 23.1238 19.5863 23.0775 19.6559C23.0312 19.7254 23.0313 19.8181 23.0313 19.9108C23.0313 20.0963 23.0775 20.2353 23.1469 20.3281C23.2163 20.3976 23.332 20.4671 23.4708 20.5367Z"
                    fill="white"
                  />
                  <path
                    d="M52.5537 36.5766C52.3223 35.8812 51.9521 35.2785 51.42 34.7454L46.6074 29.9011C46.0753 29.3679 45.4506 29.113 44.7102 29.113C44.3863 29.113 44.0855 29.1593 43.8078 29.2752C43.5071 29.3911 43.2526 29.5302 42.9981 29.7156L47.6949 34.4209C47.8106 34.5368 47.88 34.7222 47.88 34.9077C47.88 35.0931 47.8106 35.2322 47.6717 35.3713C47.5329 35.5103 47.371 35.5799 47.209 35.5799C47.0239 35.5799 46.8851 35.5103 46.7463 35.3713L35.7561 24.3613C35.5016 24.1064 35.2008 23.9209 34.8769 23.7819C34.553 23.6428 34.1828 23.5732 33.8357 23.5732C33.535 23.5732 33.2342 23.6196 32.9565 23.7355C32.6789 23.8282 32.4244 23.9673 32.193 24.1527C31.9616 24.3381 31.7534 24.5468 31.5915 24.8017C31.4295 25.0567 31.2907 25.3117 31.2213 25.613L38.8797 33.2852C38.9953 33.4011 39.0647 33.5865 39.0647 33.7719C39.0647 33.9573 38.9953 34.0964 38.8565 34.2355C38.7177 34.3746 38.5557 34.4441 38.3938 34.4441C38.2087 34.4441 38.0698 34.3746 37.931 34.2355L29.995 26.3083C29.7405 26.0534 29.4397 25.8679 29.1158 25.7289C28.6993 25.5666 28.2829 25.4971 27.8432 25.5203C27.3805 25.5666 26.9178 25.7289 26.5244 25.9838C25.9691 26.3547 25.5989 26.8878 25.437 27.5368L35.0157 37.1328C35.1314 37.2487 35.2008 37.4342 35.2008 37.6196C35.2008 37.805 35.1314 37.9441 34.9926 38.0832C34.8538 38.2222 34.6918 38.2918 34.5299 38.2918C34.3448 38.2918 34.2059 38.2222 34.0671 38.0832L26.1542 30.156C25.8997 29.9011 25.5989 29.7156 25.275 29.5766C24.9511 29.4375 24.5809 29.3679 24.2339 29.3679C23.9331 29.3679 23.6323 29.4143 23.3546 29.5302C23.077 29.6229 22.8225 29.762 22.5911 29.9474C22.3598 30.1328 22.1515 30.3415 21.9896 30.5964C21.9896 30.5964 21.9896 30.6196 21.9664 30.6196C21.6888 31.0136 21.7582 31.5468 22.1052 31.8944L31.1981 41.0037C31.3138 41.1196 31.3832 41.2819 31.3832 41.4673C31.3832 41.6295 31.3138 41.7918 31.2213 41.9309C31.0824 42.0699 30.9205 42.1626 30.7122 42.1626C30.5271 42.1626 30.3883 42.0931 30.2495 41.954L25.2056 36.9242C25.0205 36.7388 24.7892 36.5997 24.5346 36.4838C24.2801 36.3911 24.0256 36.3216 23.748 36.3216C23.4703 36.3216 23.2158 36.3679 22.9613 36.4838C22.7068 36.5997 22.4986 36.7388 22.3135 36.9242C22.1284 37.1097 21.9664 37.3183 21.8739 37.5732C21.7582 37.8282 21.7119 38.0832 21.7119 38.3613C21.7119 38.6163 21.7582 38.8944 21.8739 39.1494C21.9664 39.4044 22.1284 39.613 22.3366 39.8216L36.0569 53.0335C36.4734 53.4275 36.9593 53.7984 37.4914 54.1229C38.0236 54.4474 38.602 54.7024 39.1804 54.911C39.782 55.1196 40.3836 55.2819 40.9851 55.3977C41.5867 55.5136 42.1883 55.56 42.7667 55.56C44.178 55.56 45.4969 55.2819 46.7463 54.7487C47.9725 54.2156 49.06 53.4739 49.9855 52.5699C50.9109 51.6428 51.6282 50.5766 52.1603 49.3249C52.6925 48.0964 52.9701 46.7521 52.9701 45.3381V38.709C52.9007 37.9673 52.785 37.2719 52.5537 36.5766Z"
                    fill="white"
                  />
                  <path
                    d="M21.8042 28.9741C22.1281 28.7191 22.4983 28.5105 22.8916 28.3714C23.3081 28.2323 23.7245 28.1396 24.1641 28.1396C24.2104 28.1396 24.2567 28.1396 24.2798 28.1396C24.2798 28.1396 24.0716 27.4211 24.303 27.027C24.141 26.9575 23.979 26.9111 23.7708 26.9111C23.4006 26.9111 23.0536 26.9807 22.7296 27.1197C22.4057 27.2588 22.105 27.4674 21.8736 27.6992C21.6191 27.9542 21.434 28.2323 21.2952 28.5568C21.1563 28.8813 21.0869 29.229 21.0869 29.5999C21.0869 29.6231 21.11 29.6462 21.11 29.6926C21.3183 29.4376 21.5497 29.1827 21.8042 28.9741Z"
                    fill="white"
                  />
                  <path
                    d="M19.9306 37.2488C20.1851 37.5037 20.5785 37.411 20.7173 37.0865C20.8793 36.6925 21.1338 36.3448 21.4346 36.0435C21.7353 35.7653 22.0593 35.5335 22.4526 35.3713C22.5452 35.3249 22.6377 35.3018 22.7302 35.2554C23.0773 35.1395 23.193 34.7223 22.9385 34.4673L20.7404 32.2653L20.4397 31.964C20.324 31.8481 20.2777 31.6627 20.324 31.5004L20.4397 31.0832C20.4397 31.06 20.4628 31.06 20.4628 31.0368C20.4165 31.0368 20.3702 31.0137 20.3008 31.0137C19.9306 31.0137 19.5604 31.0832 19.2365 31.2223C18.9126 31.3614 18.6118 31.57 18.3804 31.8249C18.1259 32.0799 17.9408 32.358 17.802 32.7057C17.6632 33.0302 17.5938 33.3779 17.5938 33.7719C17.5938 34.5137 17.8483 35.1627 18.3804 35.6958L19.9306 37.2488Z"
                    fill="white"
                  />
                  <path
                    d="M35.1545 53.8447L21.4573 40.6559C21.1334 40.3546 20.8789 39.9838 20.717 39.5665C20.555 39.1725 20.4856 38.7553 20.4856 38.3381C20.4856 38.2453 20.5319 38.1758 20.5319 38.0831C20.5319 38.0831 20.5087 38.0831 20.5087 38.0599C20.393 37.9904 20.2542 37.944 20.1154 37.8977C19.9766 37.8513 19.8146 37.8281 19.6064 37.8281C19.3287 37.8281 19.0742 37.8745 18.8197 37.9904C18.5652 38.1063 18.357 38.2453 18.1719 38.4308C17.9868 38.6162 17.8248 38.848 17.7323 39.0798C17.6166 39.3348 17.5703 39.5897 17.5703 39.8679C17.5703 40.1228 17.6166 40.401 17.7323 40.6559C17.848 40.9109 17.9868 41.1195 18.1719 41.3281L28.3753 51.55C28.9537 52.1295 29.6016 52.6394 30.3882 53.1262C31.1518 53.6129 31.9616 54.0301 32.8176 54.401C33.6737 54.7718 34.5529 55.05 35.4321 55.2354C36.2188 55.4209 36.9591 55.4904 37.6533 55.5136C37.3756 55.3745 37.0748 55.2818 36.8203 55.1195C36.1956 54.7255 35.6403 54.3083 35.1545 53.8447Z"
                    fill="white"
                  />
                </motion.svg>
              );
            default:
              return null;
          }
        })}
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  bottom: -2rem;
  overflow: visible;
  pointer-events: none;
  position: absolute;
  right: 0;

  > div {
    position: relative;

    svg {
      bottom: 0;
      position: absolute;
      right: 0;
      width: 2rem;
    }
  }
`;

export default ReactionStream;
